/**
 * Helpers Functions
 */
import { store } from '@/store/store'
import { parseISO } from 'date-fns'
import numeral from 'numeral'
import { notAvailable, parseIsoDate } from '@/consts/main'
import { SERVICE_ROUTE_TYPE_BY_SERVICE_TYPE, SERVICE_TYPES } from '@/consts/service'
import { serviceBasePath } from '@/helpers/serverPaths'

/**
 * Check if object is empty
 * @param obj {object} - Object to check
 * @returns {boolean} - true if empty
 */
export function isEmpty (obj) {
  for (let prop in obj) {
    if (obj.hasOwnProperty(prop)) {
      return false
    }
  }
  return JSON.stringify(obj) === JSON.stringify({})
}

/**
 * Text truncate
 * @param str {string} - Text to truncate
 * @param length {number} - String length
 * @param ending {string} - End of truncated string
 * @returns str {string} - Formated string
 */
export function textTruncate (str, length = 100, ending = '...') {
  if (str.length > length) {
    return str.substring(0, length - ending.length) + ending
  } else {
    return str
  }
}

/**
 * Function to return current app layout
 */
export function getCurrentAppLayout (router) {
  let location = router.history.current.fullPath
  let path = location.split('/')
  return path[1]
}

/**
 * Open cPanel session
 * @param id {string} - service id
 * @param username {string} - only for reseller subaccounts
 */
export async function launch_cpanel (id, username = '') {
  let w = window.open()
  try {
    // w.document.write('Connecting to cPanel...')
    w.location = await store.dispatch('getcpanelloginurl', { id, username })
  } catch (e) {
    w.document.write('Error Connecting')
    w.close()
    store.dispatch('reportError', 'Unable to connect to cPanel.')
  }
}

/**
 * Open phpmyAdmin
 * @param id {string} service id
 * @param _username {string | undefined} reseller's subaccount
 */
export async function launch_phpmyadmin (id, _username) {
  let w = window.open()
  try {
    w.document.write('Connecting to phpMyAdmin...')
    w.location = await store.dispatch('getPhpMyAdmiUurl', { id, _username })
  } catch (e) {
    w.document.write('Error Connecting')
    w.close()
    store.dispatch('reportError', 'Unable to connect to cPanel.')
  }
}

/**
 * Open cPanel Fileman
 * @param id {string} service_id
 * @param username {string} reseller subaccount username
*/
export async function launch_fileman (id, username) {
  console.toLocaleString(id, username)
  let w = window.open()
  try {
    w.document.write('Connecting to File Manager...')
    w.location = await store.dispatch('getFilemanLoginUrl', { serviceId: id, username })
  } catch (e) {
    w.document.write('Error Connecting')
    w.close()
    store.dispatch('reportError', 'Unable to connect to File Manager.')
  }
}

/**
 * Launch Webmail session
 * @param id {string} service id
 * @param email {string} email address
 */
// cPanel's path for Roundcube; passed as `goto_uri` it skips the webmail
// client picker and opens the mailbox straight in Roundcube.
export const WEBMAIL_ROUNDCUBE = '/3rdparty/roundcube/'

export async function launch_webmail (id, email, gotoUri = '') {
  let response
  try {
    response = await store.dispatch('getWebmailLoginUrl', { serviceID: id, email })
  } catch (e) {
    store.dispatch('reportError', 'Unable to connect to cPanel.')
    return
  }
  const windowName = 'w' + Math.floor(Math.random() * 100000).toString()
  const session = response.body.replace('session=', '')
  const form = document.createElement('form')
  form.setAttribute('method', 'post')
  form.setAttribute('action', response.url)
  form.setAttribute('target', windowName)
  const hiddenField = document.createElement('input')
  hiddenField.setAttribute('type', 'hidden')
  hiddenField.setAttribute('name', 'session')
  hiddenField.setAttribute('value', session)
  form.appendChild(hiddenField)
  if (gotoUri) {
    const gotoField = document.createElement('input')
    gotoField.setAttribute('type', 'hidden')
    gotoField.setAttribute('name', 'goto_uri')
    gotoField.setAttribute('value', gotoUri)
    form.appendChild(gotoField)
  }
  document.body.appendChild(form)
  window.open('', windowName)
  form.submit()
}

// export function launch_whm (ip) {
//   try {
//     if (ip === '') {
//       return store.dispatch('reportError', 'Error Connecting. Unable to connect to WHM.')
//     }
//
//     let w = window.open()
//     w.document.write('Connecting to WHM...')
//     // w.location = await store.dispatch('getwhmloginurl', id)
//     w.location = `http://${ip}/whm`
//   } catch (e) {
//     store.dispatch('reportError', e.message)
//   }
// }

/**
 * Open WHM session
 * @param id
 */
export async function launch_whm (id) {
  const w = window.open()
  try {
    if (id === '') {
      return store.dispatch('reportError',
        'Error Connecting. Unable to connect to WHM.')
    }

    w.document.write('Connecting to WHM...')
    w.location = await store.dispatch('getwhmloginurl', id)
  } catch (e) {
    w.document.write('Error Connecting')
    w.close()
    store.dispatch('reportError', 'Unable to connect to WHM.')
  }
  //
  // let w = window.open()
  // try {
  //   w.document.write('Connecting to WHM...')
  //   w.location = await store.dispatch('getwhmloginurl', id)
  // } catch (e) {
  //   w.document.write('Error Connecting')
  //   w.close()
  //   store.dispatch('reportError', 'Unable to connect to WHM.')
  // }
}

/**
 * Open GreenGeeks Seal Verification window
 */
export async function launch_ggseal () {
  const w = window.open()
  // w.document.write('Verifying website...')
  w.location = 'https://my.greengeeks.com/seal/'
}

/**
 * A list of domains without possibility of custom NS registration
 * @type {Array}
 */
export const domains_without_custom_ns = [
  {
    value: '.eu'
  },
  {
    value: '.de'
  }
]

/**
 * Return color in depend of status text
 * @param status {string}
 * @returns {string}
 */
export function statusColor (status) {
  const greenValues = ['Paid', 'Active', 'Installed', 'online', '1', 1, 'completed', 'saved', 'completed', 'refunded', 'In Progress', 'resolved']
  const greyValues = ['Cancelled', 'closed', 'Closed', 'Aborted']
  const redValues = ['responserequired', 'escalated', 'flagged', 'scheduled', 'customerescalated', 'Suspended', 'Refunded', 'Response Required', 'paymentfailed', 'requirepayment', 'pending', 'failed', 0]
  const blueValues = ['processing']

  if (greenValues.indexOf(status) !== -1) {
    return 'primary'
  } else if (greyValues.indexOf(status) !== -1) {
    return 'grey'
  } else if (redValues.indexOf(status) !== -1) {
    return 'red'
  } else if (blueValues.indexOf(status) !== -1) {
    return 'blue'
  } else {
    return 'orange lighten-1'
  }
}

export const statusNames = {
  responserequired: 'Response Required',
  pending: 'Pending',
  saved: 'Saved',
  processing: 'Processing',
  escalated: 'Escalated',
  completed: 'Completed',
  closed: 'Closed',
  pendingrefund: 'Pending Refund',
  refunded: 'Refunded',
  flagged: 'Flagged',
  donotrenew: 'Do Not Renew',
  customerescalated: 'Customer Escalated',
  scheduled: 'Scheduled',
  requirepayment: 'Payment Required',
  paymentfailed: 'Payment Failed',
  Aborted: 'Aborted',
}

/**
 * Check service by status if it's managable or not
 * @param service {string}
 * @returns {boolean}
 */
export function serviceManagable (service) {
  const notManagableStatuses = ['Cancelled', 'Pending']
  return notManagableStatuses.indexOf(service.status) === -1 // && !service.load_error
}

/**
 * Return file size in readable format
 * @param bytes
 * @param si
 * @returns {string}
 */
export function humanFileSize (bytes, si = true) {
  if (bytes === 'unlimited') return 'Unlimited'
  const thresh = si ? 1000 : 1024
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B'
  }
  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  do {
    bytes /= thresh
    ++u
  } while (Math.abs(bytes) >= thresh && u < units.length - 1)
  return bytes.toFixed(0) + ' ' + units[u]
}

/**
 * Finding what percent one number is of another
 * @param a
 * @param b
 * @returns {number}
 */
export function percent (a, b) {
  if (!Number.isInteger(a) || !Number.isInteger(b)) return 0
  if (!b) return 0
  return Math.round(a / b * 100)
}

export function moneyFormat (val) {
  return numeral(val).format('$0,0.00')
}

/**
 * Function to objects deep copy
 * @param data {object}
 * @returns {any}
 */
export function deepCopy (data) {
  return JSON.parse(JSON.stringify(data))
}

// generateNoipDomain() was removed in GG-994. It hardcoded the no-ip domain, so
// every build — production included — offered customers a hostname on the ggsdev
// development domain. The API now returns `suggested_domain` on GET /service,
// derived from the reservation IP with the domain configured per deployment.

export function parseRenewalDate (dateStr = null) {
  const parsedDate = parseISO(dateStr)
  return parseIsoDate.INVALID_DATE === parsedDate.toString() ? notAvailable.SHORT : parsedDate
}

export function formatRenewalDate (date, formatStr = 'yyyy-mm-dd') {
  if (date === notAvailable.SHORT) {
    return date
  }
  let year = date.getFullYear()
  let month = date.getMonth() + 1
  let dt = date.getDate()
  if (dt < 10) {
    dt = '0' + dt
  }
  if (month < 10) {
    month = '0' + month
  }
  return month + '/' + dt + '/' + year
}

export const breadcrumbTemplate = `<v-breadcrumbs v-if="$breadcrumbs.length" class="py-2 gg-breadcrumbs" :items="$breadcrumbs">
      <template slot="item" slot-scope="props">
        <router-link :to="props.item.path" class="text-uppercase">
          {{ typeof props.item.meta.breadcrumb === 'function' ? props.item.meta.breadcrumb($route.params) : props.item.meta.breadcrumb }}
        </router-link>
      </template>
      <template slot="divider"><v-icon>mdi-chevron-right</v-icon></template>
    </v-breadcrumbs>`

/**
 * Wrapper around console.log()
 * @param message {string} - Message that should be print in console
 */
export const logger = (message) => {
  if (process.env.NODE_ENV !== 'production' && process.env.VUE_APP_CACHE_LOGS !== 'false') {
    let styles = [
      'display: block',
      'background: grey',
      'color: white',
      'text-shadow: 0 1px 0 rgba(0, 0, 0, 0.3)',
      'font-weight: bold',
      'padding: 10px'
    ].join(';')
    console.log(`%c${message} `, styles)
  }
}

/**
 * Decode special characters to html
 * @param str
 * @returns {*}
 */
// export const decodeHtmlCharCodes = (str = '') => { str.replace(/(&#(\d+);)/g, (match, capture, charCode) => String.fromCharCode(charCode)) }
export const decodeHtmlCharCodes = (str = '') => {
  return str.replace(/(&#(\d+);)/g, (match, capture, charCode) => String.fromCharCode(charCode))
}

/**
 * Generate custom table skeleton type for v-skeleton-loader
 * @param cols Number - cols count
 * @param rows Number - rows count
 * @returns {*}
 */
export function customTableSkeletonType (cols = 6, rows = 6) {
  return {
    table: 'table-heading, table-thead, table-tbody, table-tfoot',
    'table-heading': 'heading, text',
    'table-thead': 'heading@' + cols,
    'table-tbody': 'table-row-divider@' + rows,
    'table-row-divider': 'table-row, divider',
    'table-row': 'table-cell@' + cols,
    'table-cell': 'text',
    'table-tfoot': 'text@2, avatar@2'
  }
}

/**
 * Check if amapi response contains an error.
 * @param response Object
 * @returns Boolean
 */
export function responseFailed (response) {
  if (typeof response !== 'object') {
    throw new Error('Invalid response.')
  }
  return response.result !== 'success'
}

/**
 * Get the error message from amapi response.
 * @param response Object
 * @returns String
 */
export function responseErrorMessage (response) {
  const defaultMsg = 'Oops! Something went wrong.'
  if (typeof response !== 'object') return defaultMsg
  return response.resultmsg || defaultMsg
}

/**
 * Get the error message from amapi response if response failed.
 * @param response Object
 * @returns String, Boolean
 */
export function responseError (response) {
  if (responseFailed(response)) {
    return responseErrorMessage(response)
  }
  return false
}

export function openChat () {
  try {
    window.LC_API.open_chat_window()
  } catch (e) {
    if (e.name === 'ReferenceError') {
      console.warn('Chat is not available.')
    }
  }
}

export function hideChat () {
  try {
    // https://developers.livechat.com/docs/extending-chat-widget/javascript-api/v1.0#hide-the-chat-window
    window.LC_API.hide_chat_window()
  } catch (e) {
    if (e.name === 'ReferenceError') {
      console.warn('Chat is not available.')
    }
  }
}

export function manageServiceLink (service) {
  const types = {
    dserver: 'dedicated',
    vserver: 'vps',
    cpanel: 'shared',
    whm: 'reseller',
    kvm: 'kvm',
  }
  const serviceType = types[service.type]
  return serviceBasePath(serviceType, service.id)
}

/**
 * Checks if user profile completely filled. Returns false if any of the following fields are missing:
 * Address 1
 * City
 * State
 * Zip Code
 * Country
 * @returns {boolean}
 */
export function isProfileFilled () {
  const profile = store.getters.SessionProfile

  return !!profile?.address1 && !!profile?.city && !!profile?.postcode && !!profile?.country
}

/**
 * Checks if API version is not outdated
 * @param version { Number } Cached API version
 */
export function checkApiVersion (version = 0) {
  const savedVersion = Number(store.getters.apiVersion)
  const currentVersion = Number(version)

  if (savedVersion === 0) {
    if (currentVersion) {
      store.commit('SET_API_VERSION', currentVersion)
    }

    return
  }

  if (currentVersion != savedVersion) {
    store.commit('OPEN_OUTDATED_CACHE_MODAL')
  }
}

/**
 * Generate a random name with specified length
 * @param length { Number } Name length
 */
export function randomName (length = 14) {
  let result = []
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  const charactersLength = characters.length
  for (let i = 0; i < length; i++) {
    result.push(characters.charAt(Math.floor(Math.random() * charactersLength)))
  }
  return result.join('')
}

export async function scrollTo (el, container = '.v-main__wrap') {
  await this.$vuetify.goTo(el, {
    duration: 500,
    easing: 'easeInOutCubic',
    container,
    offset: 20
  })
}

export function validateHostname (string) {
  const regex = /[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+/

  return regex.test(string)
}

export function validateIP4 (string) {
  const regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/

  return regex.test(string)
}

export function validateIP6 (string) {
  const regex = /(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/
  return regex.test(string)
}

/**
 * @param current {String} version for compare. ex: '1.2.1'
 * @param latest {String} version to compare with. ex: '1.3.0'
 * @return {Boolean}
**/
export function versionNeedsUpgrade (current, latest) {
  const [majorCurr = 0, minorCurr = 0, patchCurr = 0] = current.split('.')
  const [major = 0, minor = 0, patch = 0] = latest.split('.')
  return (major >= majorCurr && minor >= minorCurr && patch > patchCurr) ||
    (major >= majorCurr && minor > minorCurr) ||
    major > majorCurr
}

export function generateStr (length = 12, charset = 'abcdefghijklmnopqrstuvwxyz0123456789') {
  let str = ''
  for (let i = 0, n = charset.length; i < length; ++i) {
    str += charset.charAt(Math.floor(Math.random() * n))
  }
  return str
}

/**
 * Class list for v-icon component for country flag display.
 * @param country {String} ISO-2 value i.e. 'CA' or 'US'
 * @return {String}
 **/
export function iconFlag (country) {
  let iconClass = ''
  switch (country) {
    case 'US':
      iconClass = 'flag-icon flag-icon-us'
      break
    case 'CA':
      iconClass = 'flag-icon flag-icon-ca'
      break
    case 'EU':
      iconClass = 'flag-icon flag-icon-nl'
      break
    case 'SG':
      iconClass = 'flag-icon flag-icon-sg'
      break
  }
  return iconClass
}

/**
 * Returns the string color value regarding to the migration status.
 * @param {string} status - The migration status.
 * @return {string} The color associated with the migration status.
 */
export function migrationStatusColor (status) {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'orange'
    case 'review':
      return 'orange'
    case 'active':
      return 'red'
    case 'resolved':
      return 'green'
    case 'closed':
      return 'grey'
    default:
      return 'grey'
  }
}

export const stringIntToBytes = (stringValue) => {
  if (typeof stringValue !== 'string') {
    console.warn(`Argument "stringValue" must has type "string": ${stringValue}`)
    return 0
  }
  const value = stringValue.trim()
  const pattern = /^(?<intPart>[0-9]+) ?(?<stringEnding>[A-Za-z]+)$/
  const result = value.match(pattern)
  if (!result.groups) {
    console.warn(`Unexpected argument "stringValue" was passed into function stringIntToBytes: ${stringValue}`)
    return 0
  }
  if (!result.groups.intPart) {
    console.warn(`The argument "stringValue" in function "stringIntToBytes" must starts with numbers: ${stringValue}`)
    return 0
  }
  if (!result.groups.stringEnding) {
    console.warn(`The argument "stringValue" doesn't contain units: ${stringValue}`)
    return parseInt(stringValue, 10)
  }
  switch (result.groups.stringEnding) {
    case 'M':
      return parseInt(result.groups.intPart, 10) * 1000000
    default:
      return parseInt(result.groups.intPart, 10)
  }
}

export const getServiceRouteType = ({ type: serviceType }) => {
  switch (serviceType) {
    case SERVICE_TYPES.CPANEL:
      return SERVICE_ROUTE_TYPE_BY_SERVICE_TYPE.CPANEL
    case SERVICE_TYPES.RESELLER:
      return SERVICE_ROUTE_TYPE_BY_SERVICE_TYPE.RESELLER
    case SERVICE_TYPES.VPS:
      return SERVICE_ROUTE_TYPE_BY_SERVICE_TYPE.VPS
    case SERVICE_TYPES.DEDICATED:
      return SERVICE_ROUTE_TYPE_BY_SERVICE_TYPE.DEDICATED
    case SERVICE_TYPES.KVM:
      return SERVICE_ROUTE_TYPE_BY_SERVICE_TYPE.KVM
    default:
      return ''
  }
}
